import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getPrimaryGameImage } from "@/lib/gameImages";
import type { Game } from "@/types/game";

export interface CartItem {
  gameId: string;
  title: string;
  sku: string;
  price: number | null;
  imageUrl: string | null;
  slug: string;
  quantity: number;
}

interface CartContextValue {
  items: CartItem[];
  itemCount: number;
  subtotal: number;
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  addItem: (item: Omit<CartItem, "quantity">, quantity?: number) => void;
  removeItem: (gameId: string) => void;
  setQuantity: (gameId: string, quantity: number) => void;
  clearCart: () => void;
}

const STORAGE_KEY = "rsg-cart-v1";

const CartContext = createContext<CartContextValue | undefined>(undefined);

const loadCart = (): CartItem[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((i) => i && typeof i.gameId === "string" && typeof i.sku === "string");
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(() => (typeof window === "undefined" ? [] : loadCart()));
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* storage unavailable */
    }
  }, [items]);

  // Stored bag items are a snapshot; refresh them from the database so image,
  // title, price and slug always match the current game record.
  useEffect(() => {
    let cancelled = false;
    const ids = items.map((i) => i.gameId);
    if (ids.length === 0) return;
    (async () => {
      const { data } = await supabase
        .from("games")
        .select("id, title, game_image_url, cost, gamecrafter_sku, gallery_images:game_gallery_images(*)")
        .in("id", ids);
      if (cancelled || !data) return;
      const currentGames = data as unknown as Game[];
      setItems((prev) =>
        prev.map((i) => {
          const g = currentGames.find((d) => d.id === i.gameId);
          if (!g) return i;
          return {
            ...i,
            title: g.title ?? i.title,
            imageUrl: getPrimaryGameImage(g),
            price: g.cost ?? i.price,
            sku: (g.gamecrafter_sku as string) ?? i.sku,
          };
        }),
      );
    })();
    return () => {
      cancelled = true;
    };
    // Only refresh when the set of game ids changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.map((i) => i.gameId).sort().join(",")]);

  const addItem = useCallback((item: Omit<CartItem, "quantity">, quantity = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.gameId === item.gameId);
      if (existing) {
        return prev.map((i) => (i.gameId === item.gameId ? { ...i, ...item, quantity: i.quantity + quantity } : i));
      }
      return [...prev, { ...item, quantity }];
    });
  }, []);

  const removeItem = useCallback((gameId: string) => {
    setItems((prev) => prev.filter((i) => i.gameId !== gameId));
  }, []);

  const setQuantity = useCallback((gameId: string, quantity: number) => {
    setItems((prev) =>
      quantity <= 0
        ? prev.filter((i) => i.gameId !== gameId)
        : prev.map((i) => (i.gameId === gameId ? { ...i, quantity: Math.min(quantity, 99) } : i)),
    );
  }, []);

  const clearCart = useCallback(() => setItems([]), []);
  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  const value = useMemo<CartContextValue>(() => {
    const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
    const subtotal = items.reduce((sum, i) => sum + (i.price ?? 0) * i.quantity, 0);
    return {
      items,
      itemCount,
      subtotal,
      isOpen,
      openCart,
      closeCart,
      addItem,
      removeItem,
      setQuantity,
      clearCart,
    };
  }, [items, isOpen, openCart, closeCart, addItem, removeItem, setQuantity, clearCart]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
};
